<script setup lang="ts">
import { useFormValidation } from "../../../../composables/useFormValidation";
import { required } from "../../../../utils/validation";
import TextInput from "../../../../components/ui/form/TextInput.vue";
import CheckInput from "../../../../components/ui/form/CheckInput.vue";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { Permission } from "../../../../types/auth";
import { ref, onMounted } from "vue";
import ModalFormTemplate from "../../../../components/ui/Templates/ModalFormTemplate.vue";
import NumberInput from "../../../../components/ui/form/NumberInput.vue";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import PhoneInput from "../../../../components/ui/form/PhoneInput.vue";
import DateInput from "../../../../components/ui/form/DateInput.vue";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string | null;
  endPoint: string;
  permissions?: Permission[];
  listCreateName?: string;
}>();

const emit = defineEmits<{
  (e: "save", data: any): void;
  (e: "close"): void;
}>();

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  resetForm,
  setFormData,
} = useFormValidation({
  code: props.data?.code || "",
  name: props.data?.name || props.listCreateName || "",
  vendor_types_id: props.data?.vendor_types_id || "",
  registration_no: props.data?.registration_no || "",
  registration_date: props.data?.registration_date || "",
  tax_identification_no: props.data?.tax_identification_no || "",
  countrys_id: props.data?.countrys_id || "",
  address: props.data?.address || "",
  mobile: props.data?.mobile || "",
  email: props.data?.email || "",
  url: props.data?.url || "",
  credit_terms_id: props.data?.credit_terms_id || "",
  credit_limit: props.data?.credit_limit || "",
  allow_hire_purchase: props.data?.allow_hire_purchase || "",
  accounts_payable_id: props.data?.accounts_payable_id || "",
  buyers_id: props.data?.buyers_id || "",
  send_invoice_via: props.data?.send_invoice_via || "",
  active: props.data?.active || true,
  uid: props.data?.uid || "",
});

setFieldRules("name", [required("Name is required")]);
setFieldRules("vendor_types_id", [required("Vendor Type is required")]);
setFieldRules("credit_terms_id", [required("Credit Term is required")]);
setFieldRules("credit_limit", [required("Credit Limit is required")]);
setFieldRules("send_invoice_via", [
  required("Select How the Invoice shall be sent"),
]);

const handleSubmit = async (close: boolean, newRecord: boolean) => {
  if (validateForm()) {
    const action = formData.value.uid != "" ? "update" : "create";
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + action,
      formData.value
    );
    if (response.success == true) {
      Toast.show("saved successfully");
      emit("save", response.data);
      if (close) {
        emit("close");
      } else if (newRecord) {
        resetForm();
      } else {
        setFormData(response.data);
      }
    }
  }
};

const handleGetRecord = async () => {
  if (!props.data)
    return
  const params = {
    id: props.data?.uid
  }
  const response = await api.post((props.endPoint.slice(-1) == '/' ? props.endPoint : props.endPoint + '/') + "record", params);
    if (response.success == true) {
      setFormData(response.data);
    }
};

const isReadOnly = ref(true);
onMounted(() => {
  isReadOnly.value = props.mode == 'view'
  if (props.mode == 'create') {
      resetForm()
    } else if (props.data) {
      handleGetRecord()
    }
})


const sendInvoiceViaList = [
  { id: "Print", name: "Print" },
  { id: "Email", name: "Email" },
];

const handleVendorType = (data: any) => {
  if(data) {
    fields.value.buyers_id.value = data.buyers_id;
    fields.value.send_invoice_via.value = data.send_invoice_via;
    fields.value.accounts_payable_id.value = data.accounts_payable_id;
  }
};
</script>
<template>
  <ModalFormTemplate
    :data="data"
    :mode="mode"
    :title="title"
    :endPoint="endPoint"
    :perm="permissions"
    :listCreateName="listCreateName"
    :tabList="[]"
    :formData="formData"
    @save="handleSubmit"
    @close="$emit('close')"
  >
    <template #form>
      <div class="grid md:grid-cols-3 gap-6">
        <TextInput
          v-model="fields.name.value"
          label="Name"
          class="col-span-2"
          :readonly="isReadOnly"
          :error="fields.name.touched ? fields.name.error : null"
          @blur="fields.name.touched = true"
        />

        <SearchSelect
          v-model="fields.vendor_types_id.value"
          label="Vendor Type"
          end-point="procurement/master-data/vendor-types"
          :readonly="isReadOnly"
          :error="
            fields.vendor_types_id.touched ? fields.vendor_types_id.error : null
          "
          @blur="fields.vendor_types_id.touched = true"
          @select="handleVendorType"
          :allowNewRecord="true"
        />

        <TextInput
          v-model="fields.registration_no.value"
          label="Registration No"
          :readonly="isReadOnly"
          :error="
            fields.registration_no.touched ? fields.registration_no.error : null
          "
          @blur="fields.registration_no.touched = true"
        />

        <DateInput
          v-model="fields.registration_date.value"
          label="Registration Date"
          :readonly="isReadOnly"
          :error="
            fields.registration_date.touched
              ? fields.registration_date.error
              : null
          "
          @blur="fields.registration_date.touched = true"
        />

        <TextInput
          v-model="fields.tax_identification_no.value"
          label="Tax Identification No"
          :readonly="isReadOnly"
          :error="
            fields.tax_identification_no.touched
              ? fields.tax_identification_no.error
              : null
          "
          @blur="fields.tax_identification_no.touched = true"
        />

        <SearchSelect
          v-model="fields.countrys_id.value"
          label="Country"
          end-point="system/master-data/countrys"
          :readonly="isReadOnly"
          :error="fields.countrys_id.touched ? fields.countrys_id.error : null"
          @blur="fields.countrys_id.touched = true"
          :allowNewRecord="true"
        />

        <TextInput
          v-model="fields.address.value"
          label="Address"
          class="col-span-2"
          :readonly="isReadOnly"
          :error="fields.address.touched ? fields.address.error : null"
          @blur="fields.address.touched = true"
        />

        <PhoneInput
          v-model="fields.mobile.value"
          label="Mobile"
          :readonly="isReadOnly"
          :error="fields.mobile.touched ? fields.mobile.error : null"
          @blur="fields.mobile.touched = true"
        />

        <TextInput
          v-model="fields.email.value"
          label="Email"
          class="col-span-2"
          :readonly="isReadOnly"
          :error="fields.email.touched ? fields.email.error : null"
          @blur="fields.email.touched = true"
        />

        <TextInput
          v-model="fields.url.value"
          label="Url"
          :readonly="isReadOnly"
          :error="fields.url.touched ? fields.url.error : null"
          @blur="fields.url.touched = true"
        />

        <SearchSelect
          v-model="fields.credit_terms_id.value"
          label="Credit Term"
          end-point="finance/master-data/credit-terms"
          :readonly="isReadOnly"
          :error="
            fields.credit_terms_id.touched ? fields.credit_terms_id.error : null
          "
          @blur="fields.credit_terms_id.touched = true"
          :allowNewRecord="true"
        />

        <NumberInput
          v-model="fields.credit_limit.value"
          label="Credit Limit"
          :readonly="isReadOnly"
          :min="0"
          :error="
            fields.credit_limit.touched ? fields.credit_limit.error : null
          "
          @blur="fields.credit_limit.touched = true"
        />

        <SearchSelect
          v-model="fields.accounts_payable_id.value"
          label="Payable Account"
          end-point="finance/accounting/chart-of-accounts"
          extra="Current Liabilities"
          :readonly="isReadOnly"
          :error="
            fields.accounts_payable_id.touched
              ? fields.accounts_payable_id.error
              : null
          "
          @blur="fields.accounts_payable_id.touched = true"
        />

        <SearchSelect
          v-model="fields.buyers_id.value"
          label="Buyer"
          end-point="system/security/users"
          :readonly="isReadOnly"
          :error="fields.buyers_id.touched ? fields.buyers_id.error : null"
          @blur="fields.buyers_id.touched = true"
        />

        <SearchSelect
          v-model="fields.send_invoice_via.value"
          label="Send Invoice by"
          :options="sendInvoiceViaList"
          :readonly="isReadOnly"
          :error="
            fields.send_invoice_via.touched
              ? fields.send_invoice_via.error
              : null
          "
          @blur="fields.send_invoice_via.touched = true"
        />

        <CheckInput
          v-model="fields.allow_hire_purchase.value"
          :readonly="isReadOnly"
          label="Allow Hire Purchase"
          :error="
            fields.allow_hire_purchase.touched
              ? fields.allow_hire_purchase.error
              : null
          "
          @blur="fields.allow_hire_purchase.touched = true"
        />

        <CheckInput
          v-model="fields.active.value"
          :readonly="isReadOnly"
          label="Active"
          :error="fields.active.touched ? fields.active.error : null"
          @blur="fields.active.touched = true"
        />
      </div>
    </template>
  </ModalFormTemplate>
</template>
